import React, { useState, useMemo } from "react";
import { ITournamentApplication } from "@/redux/tournaments/types";
import { Toast, Button } from "@/components";
import { useAppDispatch } from "@/redux/store";
import {
  fetchChangeApplicationStatus,
  fetchDeleteApplication,
  fetchGetTournamentApplications,
} from "@/redux/tournaments";
import styles from "./TournamentApplicationsContent.module.scss";
import emptyGameListIcon from "@/assets/img/emptyGameListIcon.svg";
import checkMarkIcon from "@/assets/icons/checkMark.svg";
import closeIcon from "@/assets/icons/closeIcon.svg";

interface TournamentApplicationsContentProps {
  applications: ITournamentApplication[];
  tournamentId: number;
  onApplicationsChange?: (applications: ITournamentApplication[]) => void;
}

const formatPhone = (phone: string | undefined | null) => {
  if (!phone) {
    return "";
  }
  const cleaned = phone.replace(/[^0-9]/g, "");
  if (cleaned.length === 11) {
    return `+7 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7, 9)}-${cleaned.slice(9, 11)}`;
  }
  return phone;
};

const formatDate = (dateString: string | undefined | null) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const copyPhoneToClipboard = async (phone: string) => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(phone);
      return true;
    } else {
      // Fallback для старых браузеров
      const textArea = document.createElement("textarea");
      textArea.value = phone;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      return true;
    }
  } catch (error) {
    console.error("Failed to copy phone:", error);
    return false;
  }
};

export const TournamentApplicationsContent: React.FC<TournamentApplicationsContentProps> = ({
  applications,
  tournamentId,
  onApplicationsChange,
}) => {
  const dispatch = useAppDispatch();
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [loadingApplicationId, setLoadingApplicationId] = useState<number | null>(null);

  // Нормализуем статусы к нижнему регистру и маппим поля из snake_case в camelCase
  const normalizedApplications = React.useMemo(() => {
    if (!applications || !Array.isArray(applications)) {
      return [];
    }
    return applications.map((app: any) => {
      // API возвращает PENDING, APPROVED, DECLINED - нормализуем к нижнему регистру
      // DECLINED преобразуем в rejected для внутреннего использования
      const originalStatus = app.status?.toLowerCase() || "pending";
      let normalizedStatus: "pending" | "approved" | "rejected";
      if (originalStatus === "declined") {
        normalizedStatus = "rejected";
      } else if (originalStatus === "approved") {
        normalizedStatus = "approved";
      } else {
        normalizedStatus = "pending";
      }
      const normalizedApp = {
        ...app,
        status: normalizedStatus,
        // Поддержка обоих форматов: contactPhone и contact_phone
        contactPhone: app.contactPhone || app.contact_phone || "",
        partnerPhone: app.partnerPhone || app.partner_phone,
        created_at: app.created_at || app.createdAt,
      };
      console.log("[TournamentApplicationsContent] Normalized application:", {
        id: normalizedApp.id,
        name: normalizedApp.name,
        contactPhone: normalizedApp.contactPhone,
        originalContactPhone: app.contactPhone,
        originalContact_phone: app.contact_phone,
        originalStatus: app.status,
        normalizedStatus: normalizedApp.status,
        allKeys: Object.keys(app),
      });
      return normalizedApp;
    });
  }, [applications]);

  if (!normalizedApplications || normalizedApplications.length === 0) {
    return (
      <div className={styles.empty}>
        <img src={emptyGameListIcon} alt="empty" className={styles.emptyImage} />
        <p className={styles.emptyText}>Заявки отсутствуют</p>
      </div>
    );
  }

  const getStatusColor = (status: string, isPaid: boolean) => {
    const isApproved = status === "approved";
    if (isApproved && isPaid) {
      return "#4CAF50"; // main_green
    } else if (isApproved && !isPaid) {
      return "#8BC34A"; // rent_green (light green)
    } else if (!isApproved && isPaid) {
      return "#757575"; // gray_dark
    } else {
      return "#F44336"; // error_light (red)
    }
  };

  const getStatusText = (status: string, isPaid: boolean) => {
    const isApproved = status === "approved";
    if (isApproved && isPaid) {
      return "Принята • Оплачена";
    } else if (isApproved && !isPaid) {
      return "Принята • Не оплачена";
    } else if (!isApproved && isPaid) {
      return "Не принята • Оплачена";
    } else {
      return "Не принята • Не оплачена";
    }
  };

  const handlePhoneClick = async (phone: string) => {
    const success = await copyPhoneToClipboard(phone);
    if (success) {
      setToastMessage("Телефон был успешно скопирован");
      setShowToast(true);
    }
  };

  const handleStatusChange = async (
    application: ITournamentApplication,
    status: "approved" | "rejected"
  ) => {
    try {
      setLoadingApplicationId(application.id);
      await dispatch(
        fetchChangeApplicationStatus({
          tournamentId,
          type: application.type,
          applicationId: application.id,
          status,
          isPaid: application.is_paid,
        })
      ).unwrap();

      // Обновляем список заявок
      const response = await dispatch(
        fetchGetTournamentApplications(tournamentId)
      ).unwrap();
      const updatedApplications = response.applications || [];
      if (onApplicationsChange) {
        onApplicationsChange(updatedApplications);
      }

      setToastMessage(
        status === "approved" ? "Заявка одобрена" : "Заявка отклонена"
      );
      setShowToast(true);
    } catch (error) {
      console.error("Error changing application status:", error);
      setToastMessage("Ошибка изменения статуса заявки");
      setShowToast(true);
    } finally {
      setLoadingApplicationId(null);
    }
  };

  const handleTogglePaid = async (application: ITournamentApplication) => {
    try {
      setLoadingApplicationId(application.id);
      // Для изменения оплаты статус должен быть approved или rejected, не pending
      const status = application.status === "pending" ? "approved" : application.status;
      await dispatch(
        fetchChangeApplicationStatus({
          tournamentId,
          type: application.type,
          applicationId: application.id,
          status: status as "approved" | "rejected",
          isPaid: !application.is_paid,
        })
      ).unwrap();

      // Обновляем список заявок
      const response = await dispatch(
        fetchGetTournamentApplications(tournamentId)
      ).unwrap();
      const updatedApplications = response.applications || [];
      if (onApplicationsChange) {
        onApplicationsChange(updatedApplications);
      }

      setToastMessage(
        !application.is_paid ? "Заявка отмечена как оплаченная" : "Заявка отмечена как неоплаченная"
      );
      setShowToast(true);
    } catch (error) {
      console.error("Error toggling paid status:", error);
      setToastMessage("Ошибка изменения статуса оплаты");
      setShowToast(true);
    } finally {
      setLoadingApplicationId(null);
    }
  };

  const handleDeleteApplication = async (application: ITournamentApplication) => {
    if (!window.confirm("Вы уверены, что хотите удалить эту заявку?")) {
      return;
    }

    try {
      setLoadingApplicationId(application.id);
      await dispatch(
        fetchDeleteApplication({
          tournamentId,
          type: application.type,
          applicationId: application.id,
        })
      ).unwrap();

      // Обновляем список заявок
      const response = await dispatch(
        fetchGetTournamentApplications(tournamentId)
      ).unwrap();
      const updatedApplications = response.applications || [];
      if (onApplicationsChange) {
        onApplicationsChange(updatedApplications);
      }

      setToastMessage("Заявка удалена");
      setShowToast(true);
    } catch (error) {
      console.error("Error deleting application:", error);
      setToastMessage("Ошибка удаления заявки");
      setShowToast(true);
    } finally {
      setLoadingApplicationId(null);
    }
  };

  return (
    <>
      <div className={styles.container}>
        <div className={styles.list}>
          {normalizedApplications.map((application) => {
            const isLoading = loadingApplicationId === application.id;
            const statusColor = getStatusColor(application.status, application.is_paid);
            const statusText = getStatusText(application.status, application.is_paid);
            const isApproved = application.status === "approved";

            return (
              <div
                key={application.id}
                className={styles.applicationCard}
                style={{ borderLeftColor: statusColor }}
              >
                <div className={styles.applicationHeader}>
                  <div className={styles.applicationInfo}>
                    <div className={styles.applicationName}>
                      {application.name || "Без имени"}
                    </div>
                    <div className={styles.statusBadge}>
                      <div
                        className={styles.statusIndicator}
                        style={{ backgroundColor: statusColor }}
                      />
                      <span
                        className={styles.statusText}
                        style={{ color: statusColor }}
                      >
                        {statusText}
                      </span>
                    </div>
                  </div>
                  <div className={styles.headerActions}>
                    {!isApproved && (
                      <button
                        className={styles.iconButton}
                        onClick={() => handleStatusChange(application, "approved")}
                        disabled={isLoading}
                        type="button"
                      >
                        <img src={checkMarkIcon} alt="approve" className={styles.iconImage} />
                      </button>
                    )}
                    <button
                      className={styles.iconButton}
                      onClick={() => handleDeleteApplication(application)}
                      disabled={isLoading}
                      type="button"
                    >
                      <img src={closeIcon} alt="delete" className={styles.iconImage} />
                    </button>
                  </div>
                </div>

                <div className={styles.applicationDetails}>
                  {application.contactPhone && (
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>Телефон:</span>
                      <button
                        className={styles.detailValueGreen}
                        onClick={() => handlePhoneClick(application.contactPhone)}
                        type="button"
                      >
                        {formatPhone(application.contactPhone)}
                      </button>
                    </div>
                  )}

                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Уровень:</span>
                    <span className={styles.detailValue}>
                      {application.level ? Number(application.level).toFixed(1).replace('.', ',') : "Не указан"}
                    </span>
                  </div>

                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Дата подачи:</span>
                    <span className={styles.detailValue}>
                      {formatDate(application.created_at)}
                    </span>
                  </div>
                </div>

                <div className={styles.actionsContainer}>
                  <Button
                    onClickFunc={() => handleTogglePaid(application)}
                    disabled={isLoading}
                    newStyles={application.is_paid ? styles.paymentButtonTransparent : styles.paymentButtonGreen}
                  >
                    {application.is_paid ? "Отметить не оплаченным" : "Отметить оплату"}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <Toast
        message={toastMessage}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
      />
    </>
  );
};
